<template>
  <b-modal
    id="add-or-update-modal"
    :visible="isAddActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    size="lg"
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            {{ isUpdate ? 'Update' : 'Add' }} {{ modalTitle }}
          </h5>

          <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="update_modal_form"
        >
          <b-form class="my-1" @submit.prevent="handleSubmit(onCreateOrUpdate)" @reset.prevent="resetForm">
            <b-alert v-if="errorMessage" variant="danger" :show="true">
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>
            
            <b-row>
              <b-col cols="12" md="6">
                <validation-provider #default="validationContext" name="Common Complaint" rules="required">
                  <b-form-group label-for="name">
                    <template #label>
                      <span>Common Complaint<span style="color: tomato">*</span></span>
                    </template>

                    <b-form-input
                      id="name"
                      v-model="form_data.name"
                      placeholder="Common Complaint"
                      :disabled="disableForm"
                      :state="getValidationState(validationContext)"
                    />

                    <b-form-invalid-feedback>
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-if="!isUpdate && !isReferenced"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="button"
                :disabled="disableForm"
                @click="onCreateOrUpdate(true)"
              >
                Save & Add New
              </b-button>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-primary"
                class="mr-2"
                type="submit"
                :disabled="disableForm"
              >
                {{ editingData ? 'Update' : 'Save' }} & Exit
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-danger"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect,
  BAlert, BCardText, BModal, BRow, BCol, BFormDatepicker
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import { required } from '@validations'
import { get, cloneDeep } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

const rawData = {
  name: "",
}

export default {
  components: {
    BOverlay,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormTextarea,
    BAlert,
    BCardText,
    vSelect,
    BModal,
    BRow,
    BCol,
    BFormDatepicker,
    BFormSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddActive',
    event: 'update:is-add-active',
  },
  props: {
    isAddActive: {
      type: Boolean,
      required: true,
    },
    editingData: {
      type: [Object, null],
      required: false,
      default: () => null
    },
    isReferenced: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",
      modalTitle: 'Common Complaint',
      form_data: cloneDeep(rawData),
    }
  },
  computed: {
    isUpdate(){
      return !!this.editingData
    },
    disableForm(){
      return this.editingData ? !this.can("common_complaints:CanUpdate") : !this.can("common_complaints:CanAddNew")
    },
  },
  watch: {
    editingData: {
      handler(v){
        if (v){
          this.form_data = v
        } else {
          this.form_data = cloneDeep(rawData)
        }
      }, 
      immediate: true,
      deep: true
    }
  },
  methods: {
    async onCreateOrUpdate(save_and_add = false){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        if (!this.isUpdate && !this.can("common_complaints:CanAddNew")) {
          return
        }

        if (this.isUpdate && !this.can("common_complaints:CanUpdate")) {
          return
        }

        if (this.isUpdate){
          await this.useJwt().mainService.commonComplaints.update(this.form_data.id, {
            name: this.form_data.name
          });
        } else {
          await this.useJwt().mainService.commonComplaints.create(this.form_data);
        }
        
        this.$emit("refetchData");
        if (!save_and_add){
          this.$emit('update:is-add-active', false);
        } else {
          this.resetForm();
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `${this.modalTitle} ${this.isUpdate ? 'updated' : 'added'} successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.$emit("resetUpdateData")
      this.form_data = {};
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
